import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, Upload, X, CheckCircle2, Award, Clock, ArrowLeft, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";

/** Student homework submission page — rich text + multiple file attachments
 * (≤5 MB each). Students can resubmit (overwrites previous) until the
 * teacher marks it reviewed. */
export default function HomeworkSubmit() {
  const { assignmentId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // [{file_id, filename, size}]
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // Load assignment via /me/assignments so we don't need a new endpoint.
        const [all, sub] = await Promise.all([
          api.get("/me/assignments"),
          api.get(`/me/assignments/${assignmentId}/submission`).catch(() => ({ data: { submission: null } })),
        ]);
        if (cancel) return;
        const a = all.data.assignments.find((x) => x.assignment_id === assignmentId);
        setAssignment(a || null);
        if (!a) {
          setErr("Assignment not found or not assigned to you.");
          return;
        }
        const s = sub.data.submission;
        setSubmission(s);
        if (s) {
          setText(s.text || "");
          // Fetch metadata for each existing file_id (parallel).
          if (s.file_ids?.length) {
            const metas = await Promise.all(
              s.file_ids.map((fid) => api.get(`/files/${fid}/meta`).then((r) => r.data).catch(() => null))
            );
            if (!cancel) setFiles(metas.filter(Boolean).map((m) => ({ file_id: m.file_id, filename: m.filename, size: m.size })));
          }
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [assignmentId]);

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    setErr("");
    const added = [];
    for (const f of fileList) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "submission");
      try {
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ file_id: r.data.file_id, filename: r.data.filename, size: r.data.size });
      } catch (e) {
        setErr(e?.response?.data?.detail || `Upload failed: ${f.name}`);
        break;
      }
    }
    setFiles((prev) => [...prev, ...added]);
    setUploading(false);
  };

  const removeFile = (fid) => setFiles((prev) => prev.filter((f) => f.file_id !== fid));

  const handleSubmit = async () => {
    if (!text.trim() && files.length === 0) {
      setErr("Please add text or at least one file before submitting.");
      return;
    }
    setErr("");
    setSaving(true);
    try {
      const r = await api.post(`/me/assignments/${assignmentId}/homework`, {
        text: text.trim(),
        file_ids: files.map((f) => f.file_id),
      });
      setMsg(r.data.resubmitted ? "Resubmitted successfully." : "Submitted successfully.");
      const sub = await api.get(`/me/assignments/${assignmentId}/submission`);
      setSubmission(sub.data.submission);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-sage">Loading…</div>;
  }
  if (err && !assignment) {
    return (
      <div className="min-h-screen bg-bone">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <div className="text-terracotta text-sm">{err}</div>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-sage hover:underline text-sm">← Back to dashboard</button>
        </div>
      </div>
    );
  }

  const reviewed = submission?.status === "reviewed";

  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="text-xs text-muted-foreground hover:text-sage inline-flex items-center gap-1 mb-4" data-testid="homework-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">Homework</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1" data-testid="homework-title">{assignment.title}</h1>
          {assignment.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{assignment.description}</p>}
          {assignment.due_date && (
            <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Due {assignment.due_date}
            </div>
          )}
        </div>

        {reviewed && (
          <div className="mb-5 rounded-2xl border border-sage/30 bg-gradient-to-br from-sage/5 to-white p-5" data-testid="homework-graded-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sage text-white flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl font-semibold">{submission.percentage != null ? `${submission.percentage}%` : "Reviewed"}</div>
                <div className="text-xs text-muted-foreground">Reviewed by your teacher</div>
              </div>
            </div>
            {submission.feedback && (
              <div className="mt-3 bg-white border border-border rounded-lg p-3 text-sm" data-testid="homework-feedback">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Teacher's feedback</div>
                {submission.feedback}
              </div>
            )}
          </div>
        )}

        <div className="bg-white border border-border rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Your response</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your answer here…"
              disabled={reviewed}
              data-testid="homework-text"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage min-h-[180px] disabled:opacity-70"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Attachments</label>
            <div className="mt-1 space-y-2">
              {files.map((f) => (
                <div key={f.file_id} className="flex items-center gap-3 px-3 py-2 bg-bone border border-border rounded-lg" data-testid={`homework-file-${f.file_id}`}>
                  <FileText className="w-4 h-4 text-sage" />
                  <div className="flex-1 min-w-0 text-sm truncate">{f.filename}</div>
                  <div className="text-[11px] text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</div>
                  {!reviewed && (
                    <button onClick={() => removeFile(f.file_id)} className="w-7 h-7 rounded-lg hover:bg-terracotta/10 hover:text-terracotta text-muted-foreground" data-testid={`homework-file-remove-${f.file_id}`}>
                      <Trash2 className="w-3.5 h-3.5 mx-auto" />
                    </button>
                  )}
                </div>
              ))}
              {!reviewed && (
                <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-border rounded-lg hover:border-sage cursor-pointer transition">
                  <Upload className="w-5 h-5 text-sage" />
                  <div className="text-sm">{uploading ? "Uploading…" : "Click to upload files (PDF, image, DOC, up to 5 MB each)"}</div>
                  <input type="file" multiple className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} data-testid="homework-file-input" />
                </label>
              )}
            </div>
          </div>

          {err && <div className="text-xs text-terracotta" data-testid="homework-error">{err}</div>}
          {msg && <div className="text-xs text-sage" data-testid="homework-success">{msg}</div>}

          {!reviewed && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <div className="text-xs text-muted-foreground">
                {submission ? "You can resubmit until the teacher reviews your work." : "You can resubmit until the teacher reviews your work."}
              </div>
              <button
                onClick={handleSubmit}
                disabled={saving || uploading}
                data-testid="homework-submit"
                className="bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all"
              >
                {saving ? "Submitting…" : submission ? "Resubmit" : "Submit homework"}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
